/**
 * Shared price-cell components and formatting helpers for the
 * comparison views.
 *
 * Extracted from PriceComparisonTable.jsx so the desktop table and the
 * mobile card list (MobileComparisonList.jsx) render prices with the
 * same colour coding and OOS / Error / N/A semantics.
 *
 * Colour coding follows the follow-the-market policy (sponsor-approved,
 * rev 3 2026-08-25): colour lives on the Our Price cell only — green
 * "=" at the market floor, green "▲" above it, red "▼" below it, so
 * red is the single scan signal for products we undercut everyone
 * on — while competitor cells render neutral grey with their diffs
 * kept as information. OOS (amber), Error (purple) and N/A (grey) are
 * unchanged.
 */

import {
  calcDiffPct,
  formatDiffPct,
  formatPrice,
  ourPricePosition,
  parsePrice,
} from '../utils/priceFormat';

// ---------------------------------------------------------------------------
// Error reason metadata
// ---------------------------------------------------------------------------

/**
 * Maps the backend's error_reason tokens to a one-letter badge and a
 * tooltip explaining whether/how the error is fixable. Cells with no
 * recorded reason (historic data, our-price errors) render plain
 * "Error" as before.
 */
export const ERROR_REASON_META = {
  product_gone: {
    badge: 'U',
    label:
      'Product gone — the URL is dead, redirected, or discontinued. '
      + 'Fix: remove/replace this URL in the master scraper sheet.',
  },
  variant_mismatch: {
    badge: 'V',
    label:
      'Variant mismatch — our variant was not found on the page. '
      + 'Fix: correct our_variant in the master scraper sheet '
      + '(or the variant was retired).',
  },
  invalid_price: {
    badge: 'P',
    label:
      'Invalid price — a price was found but failed to parse or fell '
      + 'outside the valid range. Needs investigation on our side '
      + '(selector drift or threshold).',
  },
  transient: {
    badge: 'T',
    label:
      'Transient scrape failure (proxy, captcha, timeout). '
      + 'No action needed — it retries on the next run.',
  },
};

/**
 * Text pill marking a key line (flagged in the master sheet's "Key
 * Lines" column). Text rather than colour alone, so it reads in
 * greyscale and for colourblind users.
 */
export function KeyLineBadge({ className = '' }) {
  return (
    <span
      className={`inline-block rounded-full border border-amber-300 bg-amber-100
                  px-1.5 text-[10px] font-semibold uppercase tracking-wide
                  leading-4 text-amber-900 ${className}`}
      title="Key line — flagged in the master sheet"
    >
      Key
    </span>
  );
}

/**
 * Renders the purple "Error" text with an optional one-letter reason
 * badge and an explanatory tooltip. Untyped errors (no reason recorded)
 * render plain "Error" exactly as before.
 */
export function ErrorLabel({ reason, className }) {
  const meta = ERROR_REASON_META[reason];
  if (!meta) {
    return <span className={className}>Error</span>;
  }
  return (
    <span className={className} title={meta.label}>
      Error
      <span className="ml-0.5 rounded bg-purple-100 px-0.5 text-[10px] font-semibold align-top">
        {meta.badge}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Cell sub-components
// ---------------------------------------------------------------------------

/** Hover text for each our-price position state. */
const POSITION_TITLES = {
  match: 'At the market floor — equal to the cheapest in-stock competitor',
  under: 'Below every in-stock competitor — raise candidate',
  above: 'Above the cheapest in-stock competitor — being undercut',
};

/**
 * Renders our price cell.  Shows strikethrough last-known price if OOS.
 * The cell is a click-through link to our own product page (same
 * behaviour as the competitor price cells) when ``our_url`` is present.
 *
 * An available price is coloured by its position against the market
 * floor (cheapest in-stock competitor): green "=" or "▲" at/above it,
 * red "▼" below it, with the gap to the floor below. The glyphs keep
 * the states distinguishable without colour (CLAUDE.md accessibility
 * rule). With no in-stock competitor there is no floor and the price
 * stays black.
 */
export function OurPriceCell({ row }) {
  const { our_price: ps, our_url: url, last_known_price: lkp } = row.original;
  const val = parsePrice(ps?.value);

  const title = 'Open our product in new tab';

  if (ps?.status === 'oos') {
    const last = parsePrice(lkp);
    return (
      <CompetitorLink url={url} title={title}>
        <div className="flex flex-col leading-tight">
          <span className="text-gray-400 line-through text-xs">
            {last != null ? formatPrice(last) : 'OOS'}
          </span>
          <span className="text-amber-600 text-xs font-medium">OOS</span>
        </div>
      </CompetitorLink>
    );
  }

  // "Error" (purple) = our own product URL is recorded but failed to
  // render/scrape, distinct from having no price data ("N/A", grey).
  // Our-price errors carry no reason token (the CSV schema only
  // records reasons for competitor cells) but are URL errors by store
  // convention — we set the price range and variants ourselves and
  // our own server is never blocked — so untagged ones render as U.
  if (ps?.status === 'error') {
    return (
      <CompetitorLink url={url} title={title}>
        <ErrorLabel
          reason={ps?.error_reason || 'product_gone'}
          className="text-purple-600 font-medium"
        />
      </CompetitorLink>
    );
  }

  if (ps?.status === 'na' || val == null) {
    return (
      <CompetitorLink url={url} title={title}>
        <span className="text-gray-400">N/A</span>
      </CompetitorLink>
    );
  }

  const minVal =
    row.original.min_competitor?.status === 'available'
      ? parsePrice(row.original.min_competitor.value)
      : null;
  const pos = ourPricePosition(val, minVal);

  if (!pos) {
    return (
      <CompetitorLink url={url} title={title}>
        <span className="text-gray-900 font-medium">{formatPrice(val)}</span>
      </CompetitorLink>
    );
  }

  return (
    <CompetitorLink url={url} title={title}>
      <div
        className="flex flex-col leading-tight"
        title={POSITION_TITLES[pos.state]}
      >
        <span className={`font-semibold ${pos.colorClass}`}>
          <span className="mr-0.5">{pos.glyph}</span>
          {formatPrice(val)}
        </span>
        <span className={`text-xs ${pos.colorClass}`}>
          {pos.state === 'match'
            ? 'at min'
            : `${formatDiffPct(pos.diffPct)} vs min`}
        </span>
      </div>
    </CompetitorLink>
  );
}

/**
 * Wrap price content in an anchor that opens the competitor product
 * page in a new tab on left-click, or in a background tab on
 * middle-click / Ctrl-click / Cmd-click (all standard browser behaviour).
 *
 * ``rel="noopener noreferrer"`` prevents the new tab from gaining a
 * ``window.opener`` handle back to the dashboard.
 *
 * If ``url`` is falsy, the children are rendered unwrapped so cells
 * without a known URL still display correctly.
 */
export function CompetitorLink({ url, title, children }) {
  if (!url) return children;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      // Stop the row-level click bubbling (harmless today, defensive
      // in case row click handlers are added later).
      onClick={(e) => e.stopPropagation()}
      className="block hover:underline"
    >
      {children}
    </a>
  );
}

/**
 * Renders a Min or Max competitor price cell with diff badge.
 * When ``url`` is provided the cell becomes a click-through link.
 * Neutral grey by design — see the module docstring.
 */
export function MinMaxPriceCell({ priceStatus, diffPct, url }) {
  const val = parsePrice(priceStatus?.value);

  // The backend only ever emits an available price or status 'na' for
  // MIN/MAX (non-available competitor cells are excluded from the
  // aggregation), so "no available competitor price" is the single
  // non-price case here.
  if (priceStatus?.status !== 'available' || val == null) {
    return (
      <CompetitorLink url={url} title="Open competitor product in new tab">
        <span className="text-gray-400">N/A</span>
      </CompetitorLink>
    );
  }

  return (
    <CompetitorLink url={url} title="Open competitor product in new tab">
      <div className="flex flex-col leading-tight">
        <span className="font-medium text-gray-700">{formatPrice(val)}</span>
        {diffPct != null && (
          <span className="text-xs text-gray-500">{formatDiffPct(diffPct)}</span>
        )}
      </div>
    </CompetitorLink>
  );
}

/**
 * Renders an individual competitor price cell.  Neutral grey by design
 * (see the module docstring): the diff vs our price is kept as
 * information, but colour appears only on the Our Price cell.
 * When ``url`` is provided the cell becomes a click-through link.
 */
export function CompetitorPriceCell({ priceStatus, ourPriceValue, url }) {
  if (!priceStatus) {
    return (
      <CompetitorLink url={url} title="Open competitor product in new tab">
        <span className="text-gray-400">N/A</span>
      </CompetitorLink>
    );
  }

  if (priceStatus.status === 'oos') {
    return (
      <CompetitorLink url={url} title="Open competitor product in new tab">
        <span className="text-amber-600 text-xs font-medium">OOS</span>
      </CompetitorLink>
    );
  }

  // "Error" (purple) = this product was in the domain's latest scrape run
  // (a URL exists in the current master CSV) but the scrape failed — 404,
  // redirect to a listing page, server error, no price found. Distinct from
  // the missing-entry case above (product absent from the domain's latest
  // run, i.e. removed from that master CSV), which renders unlinked grey
  // "N/A", and from status 'na' below (row recorded but no price data).
  if (priceStatus.status === 'error') {
    return (
      <CompetitorLink url={url} title="Open competitor product in new tab">
        <ErrorLabel
          reason={priceStatus.error_reason}
          className="text-purple-600 text-xs font-medium"
        />
      </CompetitorLink>
    );
  }

  if (priceStatus.status === 'na') {
    return (
      <CompetitorLink url={url} title="Open competitor product in new tab">
        <span className="text-gray-400">N/A</span>
      </CompetitorLink>
    );
  }

  const val = parsePrice(priceStatus.value);
  if (val == null) {
    return <span className="text-gray-400">N/A</span>;
  }

  const pct = calcDiffPct(val, ourPriceValue);

  return (
    <CompetitorLink url={url} title="Open competitor product in new tab">
      <div className="flex flex-col leading-tight">
        <span className="font-medium text-gray-700">{formatPrice(val)}</span>
        {pct != null && pct !== 0 && (
          <span className="text-xs text-gray-500">{formatDiffPct(pct)}</span>
        )}
      </div>
    </CompetitorLink>
  );
}
